import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from '../../api';
import DosenButton from '../ui/Buttons/DosenButton';
import BrandKitEditor, { contrastRatio, isValidBrandKit } from './BrandKitEditor';
import { BrandKit, DEFAULT_BRAND_KIT } from './types';
import { vb } from './theme';
import { MOBILE_SX } from '../../mobile/mobileApp';

// Organization Settings → Brand Kit (§19, §82). Saves through the org
// settings PATCH; every NEW explainer video snapshots these colors.
const BrandKitCard: React.FC = () => {
  const [kit, setKit] = useState<BrandKit>(DEFAULT_BRAND_KIT);
  const [saved, setSaved] = useState<BrandKit | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    API.getOrganizationBrandKit()
      .then((k) => {
        if (!alive) return;
        setKit(k ?? DEFAULT_BRAND_KIT);
        setSaved(k);
      })
      .catch(() => undefined)
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const dirty = JSON.stringify(kit) !== JSON.stringify(saved ?? DEFAULT_BRAND_KIT);
  const lowContrast = contrastRatio(kit.text, kit.background) < 3;
  // Words on the fill colour: whichever kit colour (text or background) reads
  // better on it.
  const onFill =
    contrastRatio(kit.text, kit.fill) >= contrastRatio(kit.background, kit.fill) ? kit.text : kit.background;

  const cancel = () => setKit(saved ?? DEFAULT_BRAND_KIT);

  const save = async () => {
    if (!isValidBrandKit(kit)) {
      toast.error('Colors must be hex values like #1C1C1C.');
      return;
    }
    try {
      setSaving(true);
      const res: any = await API.updateOrganizationBrandKit({ brandKit: kit });
      const next = res?.data?.settings?.brandKit ?? kit;
      setKit(next);
      setSaved(next);
      toast.success('Brand kit saved. Every future video starts here.');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'The brand kit could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        background: vb.surface,
        borderRadius: '8px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
      data-testid="brand-kit-card"
    >
      {/* Header bar, as on the other Organization Settings cards. */}
      <Box
        sx={{
          px: '25px',
          py: '9px',
          minHeight: '58px',
          boxSizing: 'border-box',
          borderBottom: `1px solid ${vb.borderStrong}`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          // A phone: the buttons drop under the title when they don't fit beside it.
          [MOBILE_SX]: { px: '16px', flexWrap: 'wrap', rowGap: '8px', py: '10px' }
        }}
      >
        <Typography sx={{ color: vb.text, fontSize: 16, fontFamily: 'Inter', fontWeight: 700, flex: 1 }}>
          Brand kit
        </Typography>
        {dirty && !loading && (
          <DosenButton sizeVariant="sm" tone="basic" disabled={saving} onClick={cancel}>
            Cancel Changes
          </DosenButton>
        )}
        <DosenButton
          sizeVariant="sm"
          tone="cta"
          loading={saving}
          loadingText="Saving..."
          disabled={loading || !dirty}
          onClick={save}
        >
          Save Changes
        </DosenButton>
      </Box>
      <Box
        sx={{
          flex: 1,
          px: '25px',
          py: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          [MOBILE_SX]: { px: '16px' }
        }}
      >
        <Typography sx={{ color: vb.textSecondary, fontSize: 12, fontFamily: 'Inter', lineHeight: '16px' }}>
          Four colors used by every explainer video template: background, text, outline and the one fill that carries
          emphasis. Fonts and logo stay standard for now. Changes apply to new videos; existing videos keep their own
          colors.
        </Typography>
        {/* Editor and preview side by side when there's room, stacked when not. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 2.5,
            // The preview stretches to the editor's height, so the two end level.
            alignItems: 'stretch'
          }}
        >
          <BrandKitEditor value={kit} onChange={setKit} compact />
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
              <Typography
                sx={{
                  fontFamily: vb.font,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  color: vb.textTertiary
                }}
              >
                PREVIEW
              </Typography>
              {lowContrast && (
                <Typography sx={{ fontFamily: vb.font, fontSize: 11, color: vb.danger, textAlign: 'right' }}>
                  Text and background are low-contrast, so captions may be hard to read.
                </Typography>
              )}
            </Box>
            {/* Every element uses exactly one kit colour, at full strength:
                text for the words, outline for the bordered card, fill for
                the solid emphasis (pill, progress bar, step dot). */}
            <Box
              data-testid="brand-kit-preview"
              sx={{
                flex: 1,
                minHeight: 110,
                borderRadius: '8px',
                overflow: 'hidden',
                border: `1px solid ${vb.border}`,
                background: kit.background,
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography
                  sx={{ fontFamily: vb.font, fontWeight: 800, fontSize: 16, color: kit.text, lineHeight: 1.2 }}
                >
                  Building Trust
                </Typography>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: '999px',
                    background: kit.fill,
                    color: onFill,
                    fontFamily: vb.font,
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap'
                  }}
                >
                  KEY IDEA
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: `1.5px solid ${kit.outline}`,
                  borderRadius: '6px',
                  px: 1,
                  py: 0.75
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: kit.fill,
                    color: onFill,
                    fontFamily: vb.font,
                    fontSize: 10,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  1
                </Box>
                <Typography
                  sx={{ fontFamily: vb.font, fontSize: 12, fontWeight: 600, color: kit.text, lineHeight: 1.3 }}
                >
                  Keep the promises you make
                </Typography>
              </Box>
              <Box sx={{ height: 6, borderRadius: 3, border: `1px solid ${kit.outline}`, overflow: 'hidden' }}>
                <Box sx={{ width: '60%', height: '100%', background: kit.fill }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BrandKitCard;
